<template>
  <common-modal-section
    class="player-select-modal-section"
    :class="`player-select-modal-section--${screenOrientationType}`"
    :hasCloseButton="favoriteRacePlayerSelected"
    :hasButtons="false"
    @close="emitClose"
  >
    <template v-slot:header>
      <modal-header-parts :title="$t('RaceVideoPage.PlayerSelectModal.title')" />
    </template>

    <template v-slot:default>
      <select-list-section
        :listData="exactParticipatingPlayers"
        :selectedID="selectedPlayerId || ''"
        @selectChange="changeSelected"
      >
        <template v-slot:default="slotProps">
          <player-card-parts
            :name="slotProps.item.playerName[$i18n.locale]"
            :img="playerPictureUrl(slotProps.item)"
          />
        </template>
      </select-list-section>
    </template>

    <template v-slot:buttons>
      <atom-input-button
        :linkText="$t('common.cancel')"
        colorType="secondary"
        :isDisabled="false"
        @push="emitClose()"
      />
    </template>
  </common-modal-section>
</template>

<script lang="ts">
import { defineComponent } from '@vue/composition-api'
import CommonModalSection from '@/components/common/modal/CommonModalSection.vue'
import SelectListSection from '@/components/common/SelectList/SelectListSection.vue'
import ModalHeaderParts from '@/components/common/modal/parts/ModalHeaderParts.vue'
import PlayerCardParts from '@/components/common/card/PlayerCardParts.vue'
import AtomInputButton from '@/components/atoms/input/AtomInputButton.vue'
import StoreUtil from '@/store/StoreUtil'
import PlayerDocument from '@/store/stores/collectionModule/documents/player/PlayerDocument'
import CloudFrontUtil from '@/util/aws/CloudFrontUtil'

/**
 * レース動画再生画面: 選手選択モーダル
 */
export default defineComponent({
  name: 'PlayerSelectModalSection',
  components: {
    CommonModalSection,
    SelectListSection,
    PlayerCardParts,
    ModalHeaderParts,
    AtomInputButton,
  },
  setup() {
    const raceVideoPageStore = StoreUtil.useStore('RaceVideoPageStore')
    const { exactParticipatingPlayers, selectedPlayerId, telemetryStore } = raceVideoPageStore

    /**
     * 選手顔写真のURL
     * @param player 選手情報
     */
    const playerPictureUrl = (player?: PlayerDocument) => {
      if (!player) {
        return ''
      }
      return CloudFrontUtil.getSignedUrl(player.playerPicture)
    }

    return {
      /**
       * 大会に参加している選手一覧
       */
      exactParticipatingPlayers,
      /**
       * 現在選択されている選手のID
       */
      selectedPlayerId,
      playerPictureUrl,
      telemetryStore,
    }
  },
  props: {
    /**
     * 画面向き
     */
    screenOrientationType: {
      type: String,
      default: 'portrait-primary',
    },
    /**
     * 初期お気に入り選択フラグ
     */
    favoriteRacePlayerSelected: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    /**
     * モーダル閉じる通知
     */
    emitClose(): void {
      this.$emit('close')
    },
    /**
     * 選手が選択された際に呼び出される。
     * @param selectedItemID 選手ID
     */
    changeSelected(selectedItemID: string): void {
      this.selectedPlayerId = selectedItemID
      this.telemetryStore.clearPreviousShowedTelemetries()
      this.$emit('close', 'change')
    },
  },
})
</script>

<style lang="scss" scoped>
.player-select-modal-section {
  align-items: flex-end;
  padding-bottom: 0;
  bottom: 0;
  z-index: 25;
}
</style>

<style lang="scss">
.player-select-modal-section {
  .modal {
    &__wrapper {
      border-radius: 8px 8px 0 0;
    }

    &__mask {
      background: none;
    }

    &-header-parts {
      padding: 36px 24px 12px;
      height: 68px;
    }

    &__content {
      width: 100%;
      height: calc(#{var(--screen-height)} - 56.25vw - 122px);
      padding: 24px 0;
      overflow-x: hidden;
      overflow-y: auto;
    }
  }

  &--landscape-primary,
  &--landscape-secondary {
    position: relative;
    padding: 0;

    .modal {
      position: unset;

      &__mask {
        background: rgba(0, 0, 0, 0.4);
      }

      &__wrapper {
        position: absolute;
        max-height: none;
        width: calc(100% - 8px);
        left: 4px;
      }

      &-header-parts {
        padding: 36px 24px 12px;
        height: 68px;
      }

      &__content {
        height: calc(#{var(--screen-height)} - 116px);
      }
    }

    .select-list-item-parts {
      min-height: auto;
      padding: 5px 10px;
    }

    .player-card-parts {
      &__img {
        img {
          width: 32px;
          height: 32px;
        }
      }

      &__data {
        width: calc(100% - 40px);
      }
    }
  }
}
</style>
